<template lang="pug">
.theindex-post
  .heading-container.main-container-small(v-if="!hideHeader")
    .category {{ __(post.category?.name) }}
    h1(v-html="post.title?.rendered" v-translate="THE_INDEX_TITLES_NS")
    .author {{author}}
    .date
      span.published(v-if="post.date") {{ `${__('Published')}: ${publishedDate}` }}
      span(v-if="showModifiedDate") &nbsp;&vert;&nbsp;
      span.modified(v-if="showModifiedDate") {{ `${__('Updated')}: ${modifiedDate}` }}
    social-share.left(v-if="!$rogue.appContext")
  index-hero.post-hero(
    v-if="additional.headerPics && additional.headerPics.hero"
    :image="additional.headerPics.hero"
    :headline="post.title.rendered"
  )
  fScaleTable(v-if="showFScaleTable")
  post-content(
    v-if="!post.customCss"
    :content="post.content?.rendered"
    :namespace="`theindex_${post.id}`"
    container-class="content main-container-small"
  )
  //- .page class is to make customCss styles work
  .page(v-else)
    post-content(:content="post.content?.rendered" :namespace="`theindex_${post.id}`")
  .logos(v-if="stark")
    a(href="https://www.starkcenter.org" rel="noopener noreferrer" target="_blank")
      img(src="https://assets.roguefitness.com/q_auto,f_auto/theindex/stark-logo_llacwy.jpg" alt="Stark Center")
    a(href="https://www.utexas.edu" rel="noopener noreferrer" target="_blank")
      img(src="https://assets.roguefitness.com/q_auto,f_auto/theindex/texas-logo_uacvzf.jpg" alt="University of Texas")
  PostsGrid(
    v-if="additional.relatedContentSectionTagID"
    :query="{tags: additional.relatedContentSectionTagID}"
    :show-filters="false"
  )
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onUnmounted, ref, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import indexHero from '../components/hero.vue'
import postContent from '../components/post-content.vue'
import { THE_INDEX_TITLES_NS } from '../constants'
import type { ITheIndexPost } from '../types'
import socialShare from '@/js/components/social-share.vue'
import metadata from '@/js/helpers/metadata'
import PostsGrid from '@/js/pages/theindex/components/posts-grid.vue'
import { formatDate } from '@/js/helpers/date'
import { useRogue } from '@/js/services/Rogue'

const CSS_ELEMENT_ID = 'thieindex-customcss'

const fScaleTable = defineAsyncComponent(() => import(/* webpackChunkName: "f-scale-table" */'../components/f-scale-table.vue'))

const props = withDefaults(defineProps<{
  post: ITheIndexPost,
  authorList?: string[],
}>(), {
  authorList: () => [],
})

const rogue = useRogue()
const route = useRoute()

const hideHeader = ref<boolean>(false)

useHead(metadata({
  title       : props.post?.title?.rendered ? rogue.__(props.post.title.rendered, THE_INDEX_TITLES_NS) : rogue.__('The Index'),
  description : props.post?.excerpt?.rendered
    ? rogue.__(props.post.excerpt.rendered, THE_INDEX_TITLES_NS)
    : rogue.__('A knowledge base of product and movement information, lifestyle content and strength history.'),
  image  : props.post?.featuredImagePath || '/theindex/2023%20Headers/new%20-%202000%20x%20300/home_ia101k.jpg',
  robots : 'all',
  url    : rogue.localizedBaseURL + route.path,
}, rogue))

const additional = computed(() => JSON.parse(props.post.customJs || '{}'))
const showFScaleTable = computed<boolean>(() => ['F-Scale Research', 'F-Scale Overview | Barbell Durability'].includes(props.post.title?.rendered))
const stark = computed<boolean>(() => props.post.content?.rendered?.indexOf('https://drive.google.com/file/d/') !== -1 && props.post.categories?.indexOf(19) !== -1)
const publishedDate = computed<string>(() => formatDate(props.post.date, rogue))
const modifiedDate = computed<string>(() => formatDate(props.post.modified, rogue))
const showModifiedDate = computed<boolean>(() => publishedDate.value !== modifiedDate.value)

const author = computed<string | null>(() => {
  const name = props.post._embedded?.author?.[0]?.name
  if (!name || !props.authorList.length) return null
  const lower = name.toLowerCase()

  return props.authorList.some(auth => lower.includes(auth)) ? name : null
})

watch(() => props.post, () => {
  teardown()
  setup()
})

setup()

onUnmounted(() => {
  teardown()
})

function updateHrefsWithBaseUrlRegex(htmlContent: string, baseUrl: string): string {
  // Find href attributes that start with /
  const hrefRegex = /href="(\/[^"]+)"/g

  return htmlContent?.replace(hrefRegex, `href="${baseUrl}$1"`)
}

// WordPress content images often carry width="100%" instead of real pixel dimensions, so the
// browser has nothing to compute an aspect ratio from and reserves no space before the image
// loads 
const DEFAULT_CONTENT_IMAGE_ASPECT_RATIO = '2 / 1'

function constrainImageSizes(htmlContent: string): string {
  const responsiveImageWidths = [480, 768, 960]
  const untransformedCloudinaryImgSrc = /src="(https:\/\/assets\.roguefitness\.com\/image\/upload\/)(v\d+\/[^"]+)"/g

  return htmlContent?.replace(untransformedCloudinaryImgSrc, (_match, base, path) => {
    const srcset = responsiveImageWidths
      .map(w => `${base}w_${w},c_limit,q_auto,f_auto/${path} ${w}w`)
      .join(', ')

    return `src="${base}w_960,c_limit,q_auto,f_auto/${path}" srcset="${srcset}" sizes="(min-width: 960px) 960px, 100vw" style="aspect-ratio: ${DEFAULT_CONTENT_IMAGE_ASPECT_RATIO}; object-fit: contain;"`
  })
}

function setup(): void {
  // Update hrefs to have localized links instead of always redirecting to the US site.
  if(props.post.content) {
    props.post.content.rendered = updateHrefsWithBaseUrlRegex(props.post.content.rendered, rogue.localizedBaseURL)
    props.post.content.rendered = constrainImageSizes(props.post.content.rendered)
  }

  hideHeader.value = JSON.parse(props.post.customJs || '{}').hideHeader || false
  if (props.post.customCss && !rogue.ssr) {
    const style = document.getElementById(CSS_ELEMENT_ID) || document.createElement('style')
    style.id = CSS_ELEMENT_ID
    style.innerHTML = props.post.customCss
    document.head.appendChild(style)
  }
}

function teardown(): void {
  const style = document.getElementById(CSS_ELEMENT_ID)
  if (style) {
    style.remove()
  }
}
</script>

<style lang="scss" scoped>
.category {
  font-size: $size-xs;
  text-transform: uppercase;
  color: $color-gray-dark;
  margin-bottom: $space-xs;
}

.post-hero {
  margin-bottom: $space-xxxl;
}

.heading-container,
.video-container {
  margin-bottom: 64px;
}

.main-container-small {
  img {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
  }
}

.logos {
  text-align: center;

  a {
    display: inline-block;
    margin: $space-s;
  }
}

h1 {
  font-size: 36px;
  line-height: 1.25;
}

.date{
  font-size: $size-xs;
  color: $color-gray-x-dark;

  .published, .modified {
    font-style: italic;    
  }
}
.author{
  font-style: italic;
}
</style>
